import type { BootstrapResponse, MemberSessionInfo } from "@/lib/types/session";
import { DEFAULT_LANGUAGE } from "@shared/languages.js";

/**
 * Wer zuletzt an diesem Gerät angemeldet war (Nachtrag zu S4.3).
 *
 * **Warum es das gibt.** Die Handprüfung im Flugmodus fiel durch, und zwar
 * schon an der Tür: `GET /api/bootstrap` scheitert ohne Netz, der
 * SessionProvider setzte daraufhin die Sitzung auf „anonym", und `RequireMember`
 * schickte den Fahrer auf den Login — wo er sich ohne Netz nicht anmelden kann.
 * Die gesamte Offline-Schicht dahinter (Tagesbestand, Warteschlange, Projektion)
 * war damit unerreichbar, obwohl sie funktionierte. Ein Fahrer, dessen Handy im
 * Funkloch einmal die App aus dem Speicher wirft, stand vor einer verschlossenen
 * Tür.
 *
 * **Die Regel dahinter ist dieselbe wie in der Warteschlange** (offline/
 * outbox.ts#isPermanent): *kein Netz ist keine Antwort*. Eine Antwort mit
 * `session: null` heißt „abgemeldet" — dann wird hier gelöscht. Ein `TypeError`
 * von `fetch` heißt gar nichts, und dann gilt weiter, was zuletzt galt.
 *
 * **Das ist kein Zugang.** Der Hinweis begründet keine Berechtigung: er sagt der
 * Oberfläche, wessen Tagesbestand sie zeigen soll, mehr nicht. Jeder
 * Schreibvorgang geht über die Warteschlange an den Server und wird dort gegen
 * die echte Sitzung geprüft; ist sie abgelaufen, kommt eine 401 und der Eintrag
 * bleibt liegen, bis sich jemand neu anmeldet. Das Session-Cookie bleibt
 * unangetastet — es ist HttpOnly, und daran ändert sich nichts.
 *
 * **Nur Mitglieder.** Büro und Onboarding sind nicht offline gebaut, und ihr
 * Bootstrap trägt Kundendaten — die haben im Gerätespeicher nichts zu suchen
 * (dieselbe Linie zieht der Service Worker mit „nichts unter /api im Cache").
 * Gespeichert wird deshalb ausschließlich die Mitglieds-Sitzung, und die trägt
 * kein Geheimnis: Kennung, Mandant, Rolle, Sprache.
 */

const SCHLUESSEL = "cleverfleet.sitzung.mitglied";

/**
 * `localStorage` und nicht IndexedDB: der Hinweis ist ein kurzer String und wird
 * beim allerersten Bild gebraucht, bevor irgendetwas asynchron gelesen werden
 * kann. Ein `await` an dieser Stelle hieße, jedem Start ein leeres Bild
 * voranzustellen — und genau in dem entschiede `RequireMember` erneut auf
 * „nicht angemeldet".
 */
const lager = (): Storage | null => {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    // Safari im privaten Modus wirft beim bloßen Zugriff. Ohne Speicher gibt es
    // keinen Hinweis — die App verhält sich dann wie bisher.
    return null;
  }
};

/** Prüft eine gelesene Zeile, statt ihr zu glauben. Ein manipulierter oder veralteter Eintrag darf die App nicht in einen kaputten Zustand setzen. */
function alsMitgliedssitzung(wert: unknown): MemberSessionInfo | null {
  if (typeof wert !== "object" || wert === null) return null;
  const kandidat = wert as Record<string, unknown>;
  if (kandidat.actorType !== "member") return null;
  const zeichenkette = (feld: unknown) => typeof feld === "string" && feld !== "";
  if (!zeichenkette(kandidat.memberId) || !zeichenkette(kandidat.tenantId) || !zeichenkette(kandidat.role)) return null;
  return {
    actorType: "member",
    memberId: kandidat.memberId as string,
    tenantId: kandidat.tenantId as string,
    role: kandidat.role as string,
    language: typeof kandidat.language === "string" ? kandidat.language : DEFAULT_LANGUAGE,
    mustChangePassword: kandidat.mustChangePassword === true
  };
}

/**
 * Hält fest, wer angemeldet ist — oder löscht den Hinweis, wenn niemand es ist.
 *
 * Gerufen an der einen Stelle, an der der Bootstrap-Zustand ersetzt wird
 * (SessionContext). Damit deckt sie Anmeldung, Abmeldung und jedes Nachladen
 * gleichermaßen ab, ohne dass ein Aufrufer daran denken muss.
 */
export function merkeSitzung(session: BootstrapResponse["session"]): void {
  const speicher = lager();
  if (!speicher) return;
  try {
    if (session?.actorType === "member") speicher.setItem(SCHLUESSEL, JSON.stringify(session));
    else speicher.removeItem(SCHLUESSEL);
  } catch {
    // Voller oder gesperrter Speicher: dann eben kein Hinweis. Die Anmeldung
    // selbst hängt nicht daran.
  }
}

/** Die zuletzt gemerkte Mitglieds-Sitzung, oder `null`. */
export function letzteMitgliedssitzung(): MemberSessionInfo | null {
  const speicher = lager();
  if (!speicher) return null;
  try {
    const roh = speicher.getItem(SCHLUESSEL);
    return roh ? alsMitgliedssitzung(JSON.parse(roh)) : null;
  } catch {
    return null;
  }
}

/** Verwirft den Hinweis. Für den Abmeldeweg, der den Server nicht erreicht hat. */
export function vergissSitzung(): void {
  merkeSitzung(null);
}
